import { Component, ElementRef, HostListener, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { filter } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AgenciesService } from '../../../orval-gen/endpoints/agencies/agencies.service';
import { AgencyAgenciesList200DataItem as Agency } from '../../../orval-gen/models';
import { IconComponent, IconName } from '../../shared/components/icon/icon.component';
import { AuthService } from '../../shared/services/auth.service';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { unwrap } from '../../utils/http-utils';

const COLLAPSED_KEY = 'sidebarCollapsed';

/**
 * Authenticated layout, mirroring the AGW Developer Portal AppShell: a collapsible sidebar
 * (icon rail on desktop, off-canvas sheet on mobile), a 56px top bar with the agency switcher,
 * and the routed page inside <main>.
 */
@Component({
  selector: 'app-shell',
  imports: [RouterOutlet, RouterLink, RouterLinkActive, FormsModule, IconComponent],
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss',
})
export class ShellComponent implements OnInit {
  private readonly agenciesService = inject(AgenciesService);
  private readonly auth = inject(AuthService);
  private readonly ls = inject(LocalStorageService);
  private readonly toastr = inject(ToastrService);
  private readonly router = inject(Router);
  private readonly el = inject(ElementRef);

  readonly manageNav: { label: string; path: string; icon: IconName }[] = [
    { label: 'Dashboard', path: '/dashboard', icon: 'dashboard' },
    { label: 'Providers', path: '/providers', icon: 'plane' },
    { label: 'Applications', path: '/applications', icon: 'apps' },
    { label: 'Reports', path: '/reports', icon: 'chart' },
  ];
  readonly resources: { label: string; url: string; icon: IconName }[] = [
    { label: 'BookingPad', url: environment.bpUrl, icon: 'monitor' },
    { label: 'API docs', url: 'https://docs.airgateway.net', icon: 'plug' },
    { label: 'Help Center', url: 'https://support.airgateway.com/', icon: 'help' },
  ];

  collapsed = false;
  mobileOpen = false;
  switcherOpen = false;
  agencyQuery = '';

  email = '';
  roleLabel = '';
  agencyName = '';
  currentAgency?: Agency;
  agencies: Agency[] = [];

  ngOnInit(): void {
    this.collapsed = localStorage.getItem(COLLAPSED_KEY) === 'true';
    this.email = this.ls.getItem('email') ?? '';
    this.agencyName = this.ls.getItem('agency') ?? '';
    this.roleLabel =
      this.ls.getItem('role') === 'subagency_manager' ? 'Subagency manager' : 'Agency manager';

    this.loadAgencies();
    this.displayStoredNotification();

    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
      this.mobileOpen = false;
    });
  }

  get filteredAgencies(): Agency[] {
    const q = this.agencyQuery.trim().toLowerCase();
    if (!q) return this.agencies;
    return this.agencies.filter(
      a => a.name.toLowerCase().includes(q) || (a.iata_number ?? '').includes(q)
    );
  }

  get agencyInitial(): string {
    return (this.currentAgency?.name || this.agencyName || '?').charAt(0);
  }

  toggleSidebar(): void {
    if (window.matchMedia('(max-width: 767px)').matches) {
      this.mobileOpen = !this.mobileOpen;
      return;
    }
    this.collapsed = !this.collapsed;
    localStorage.setItem(COLLAPSED_KEY, String(this.collapsed));
  }

  toggleSwitcher(): void {
    if (!this.agencies.length) return;
    this.switcherOpen = !this.switcherOpen;
    this.agencyQuery = '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const switcher = this.el.nativeElement.querySelector('.switcher');
    if (this.switcherOpen && switcher && !switcher.contains(event.target as Node)) {
      this.switcherOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.switcherOpen = false;
    this.mobileOpen = false;
  }

  switchAgency(agency: Agency): void {
    if (agency.id === this.currentAgency?.id) {
      this.switcherOpen = false;
      return;
    }
    this.ls.setItem('agency', agency.name);
    this.ls.setItem('notificationMessage', `Switched to ${agency.name}`);
    window.location.reload();
  }

  openAccountSettings(): void {
    const url = `${environment.keycloakUrl}/realms/agents/account/#/security/signingin`;
    window.open(url, '_blank', 'noopener');
  }

  logout(): void {
    this.auth.logout();
  }

  private loadAgencies(): void {
    // The route's maximum page size, so the switcher lists every agency the agent manages in one call.
    this.agenciesService
      .agencyAgenciesList({ role: 'manager', limit: 100 })
      .pipe(unwrap())
      .subscribe({
        next: agencies => {
          if (!agencies?.length) return;
          this.currentAgency = agencies.find(a => a.id === this.ls.getItem('agencyID'));
          this.agencies = agencies.sort((a, b) => {
            if (a.id === this.currentAgency?.id) return -1;
            if (b.id === this.currentAgency?.id) return 1;
            return a.name.localeCompare(b.name);
          });
        },
        error: err => console.error('Failed to load agencies:', err),
      });
  }

  private displayStoredNotification(): void {
    const message = this.ls.getItem('notificationMessage');
    if (message) {
      this.toastr.success('', message);
      this.ls.removeItem('notificationMessage');
    }
  }
}
